import type {
  ConsignmentDetail,
  ConsignmentInvoiceDetail,
  ConsignmentReceiptDetail,
  CustomerSoa,
} from './api';

/**
 * Printable consignment paperwork (Accounting v1). Same mechanism as
 * `packing-slip.ts`: each document renders into a brand-new, fully-isolated
 * window (self-contained HTML + inline CSS) so the cashier's thermal receipt
 * print stylesheet (`@page { size: 80mm }`, `.receipt-tape` rules in
 * index.css) can't distort an A4-ish document. The window prints itself and
 * closes.
 *
 * Naming is deliberate: these are a CONSIGNMENT NOTE, a BILLING STATEMENT, a
 * PAYMENT RECORD and a STATEMENT OF ACCOUNT — never "Sales Invoice" or
 * "Official Receipt" (BIR-registered statutory document names).
 *
 * All money values arrive as decimal strings; any arithmetic here is DISPLAY
 * math only (the server owns every real total).
 */

export const esc = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c);

/** Peso display: "₱1,234.50". Falls back to the raw string if unparseable. */
export function peso(v: string | number): string {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function fmtDate(v: string | Date): string {
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
}

export const SHARED_CSS = `
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #111; margin: 0; padding: 24px; }
  h1 { font-size: 18px; letter-spacing: 0.02em; margin: 0; }
  .muted { color: #666; }
  header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #ccc; padding-bottom: 12px; }
  .code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 14px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; padding: 16px 0; border-bottom: 1px solid #eee; }
  .label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #888; margin-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 14px; }
  th, td { text-align: left; padding: 6px 4px; border-bottom: 1px solid #eee; }
  th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #888; }
  th.right, td.right { text-align: right; }
  tr.total td { border-top: 2px solid #111; border-bottom: none; font-weight: 600; }
  tr.emph td { font-weight: 700; font-size: 15px; }
  .void { color: #b91c1c; border: 2px solid #b91c1c; border-radius: 4px; padding: 2px 8px; font-weight: 700; font-size: 12px; letter-spacing: 0.1em; display: inline-block; margin-top: 6px; }
  .memo { margin-top: 16px; font-size: 13px; }
  .sigs { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; margin-top: 48px; font-size: 13px; }
  .sig-line { border-top: 1px solid #111; padding-top: 6px; margin-top: 36px; }
  .foot { margin-top: 32px; padding-top: 8px; border-top: 1px solid #eee; font-size: 11px; color: #666; }
  .big-amount { font-size: 32px; font-weight: 700; margin: 16px 0 4px; }
  @page { size: auto; margin: 12mm; }
`;

/**
 * A print window opened SYNCHRONOUSLY inside the click handler. Browsers only
 * honour `window.open` while the user gesture is still "live"; once a handler
 * awaits a fetch, the popup is blocked and `window.open` returns null. So a
 * caller that must load the document first opens the shell in the click,
 * then hands it to the print function after the data arrives.
 */
export type PrintShell = Window;

/**
 * Open the shell for a document that still has to be fetched. Returns null
 * (after telling the user) when the browser blocks the popup.
 */
export function openPrintShell(onBlocked?: (message: string) => void): PrintShell | null {
  const win = window.open('', '_blank', 'width=800,height=900');
  if (!win) {
    onBlocked?.('Allow pop-ups for this site to print documents.');
    return null;
  }
  win.document.write(
    '<!doctype html><html><head><meta charset="utf-8" /><title>Preparing document…</title></head><body style="font-family:system-ui;padding:24px;color:#666">Preparing document…</body></html>',
  );
  win.document.close();
  return win;
}

/** Write the document into `shell` (or a fresh window), auto-print and close. */
export function openPrintWindow(title: string, bodyHtml: string, shell?: PrintShell | null): void {
  const win = shell ?? window.open('', '_blank', 'width=800,height=900');
  if (!win) return;
  win.document.open();
  win.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(title)}</title>
<style>${SHARED_CSS}</style>
</head>
<body>
${bodyHtml}
<script>window.onload = function(){ window.print(); setTimeout(function(){ window.close(); }, 300); };</script>
</body>
</html>`);
  win.document.close();
}

export function docHeader(
  heading: string,
  docNo: string,
  dateLine: string,
  voided: boolean,
): string {
  return `<header>
    <div>
      <h1>${esc(heading)}</h1>
      <p class="muted" style="font-size:12px;margin:4px 0 0">${esc(dateLine)}</p>
    </div>
    <div style="text-align:right">
      <p class="code">${esc(docNo)}</p>
      ${voided ? '<span class="void">VOID</span>' : ''}
    </div>
  </header>`;
}

function memoBlock(memo: string | null | undefined): string {
  return memo && memo.trim()
    ? `<div class="memo"><p class="label">Memo</p><p style="margin:0">${esc(memo.trim())}</p></div>`
    : '';
}

// ── 1. Consignment Note (CN) ────────────────────────────────────────────────

export function printConsignmentNote(doc: ConsignmentDetail, shell?: PrintShell | null): void {
  let totalUnits = 0;
  let totalValue = 0;
  const rows = doc.lines
    .map((l) => {
      const amount = l.qty * Number(l.unitPrice); // display math only
      totalUnits += l.qty;
      totalValue += amount;
      return `<tr>
        <td>${esc(l.productName)}</td>
        <td class="muted">${l.variantName ? esc(l.variantName) : '—'}</td>
        <td class="right">${l.qty}</td>
        <td class="right">${esc(peso(l.unitPrice))}</td>
        <td class="right">${esc(peso(amount))}</td>
      </tr>`;
    })
    .join('');

  const body = `
  ${docHeader('CONSIGNMENT NOTE', doc.docNo, fmtDate(doc.createdAt), doc.status === 'void')}
  <div class="grid">
    <div>
      <p class="label">Consigned to</p>
      <p style="font-weight:600;margin:0">${esc(doc.customerName)}</p>
    </div>
    <div>
      <p class="label">From branch</p>
      <p style="font-weight:600;margin:0">${esc(doc.branchName)}</p>
    </div>
  </div>
  <table>
    <thead><tr>
      <th>Product</th><th>Variation</th>
      <th class="right">Qty</th><th class="right">Unit Price</th><th class="right">Amount</th>
    </tr></thead>
    <tbody>
      ${rows}
      <tr class="total">
        <td colspan="2">Total</td>
        <td class="right">${totalUnits}</td>
        <td></td>
        <td class="right">${esc(peso(totalValue))}</td>
      </tr>
    </tbody>
  </table>
  ${memoBlock(doc.memo)}
  <div class="sigs">
    <div><div class="sig-line">Released by</div></div>
    <div><div class="sig-line">Received by</div></div>
  </div>
  <p class="foot">Goods remain the property of the company until sold or billed.</p>`;

  openPrintWindow(`Consignment Note ${doc.docNo}`, body, shell);
}

// ── 2. Billing Statement (AR) ───────────────────────────────────────────────

export function printBillingStatement(
  inv: ConsignmentInvoiceDetail,
  shell?: PrintShell | null,
): void {
  const rows = inv.lines
    .map(
      (l) => `<tr>
        <td>${esc(l.productName)}</td>
        <td class="muted">${l.variantName ? esc(l.variantName) : '—'}</td>
        <td class="right">${l.qty}</td>
        <td class="right">${esc(peso(l.unitPrice))}</td>
        <td class="right">${esc(peso(l.lineTotal))}</td>
      </tr>`,
    )
    .join('');

  const balance = Number(inv.total) - Number(inv.amountPaid); // display math only
  const dateLine = fmtDate(inv.invoiceDate) + (inv.dueDate ? ` · Due ${fmtDate(inv.dueDate)}` : '');

  const body = `
  ${docHeader('BILLING STATEMENT', inv.docNo, dateLine, inv.status === 'void')}
  <div class="grid">
    <div>
      <p class="label">Billed to</p>
      <p style="font-weight:600;margin:0">${esc(inv.customerName)}</p>
    </div>
    <div>
      <p class="label">Status</p>
      <p style="font-weight:600;margin:0;text-transform:capitalize">${esc(inv.status)}</p>
    </div>
  </div>
  <table>
    <thead><tr>
      <th>Product</th><th>Variation</th>
      <th class="right">Qty</th><th class="right">Unit Price</th><th class="right">Amount</th>
    </tr></thead>
    <tbody>
      ${rows}
      <tr class="total">
        <td colspan="4">Total</td>
        <td class="right">${esc(peso(inv.total))}</td>
      </tr>
      <tr>
        <td colspan="4" class="muted">Amount paid</td>
        <td class="right">${esc(peso(inv.amountPaid))}</td>
      </tr>
      <tr class="emph">
        <td colspan="4">Balance</td>
        <td class="right">${esc(peso(balance))}</td>
      </tr>
    </tbody>
  </table>
  ${memoBlock(inv.memo)}
  <p class="foot">This is not an official receipt.</p>`;

  openPrintWindow(`Billing Statement ${inv.docNo}`, body, shell);
}

// ── 3. Payment Record (PAY) ─────────────────────────────────────────────────

export function printPaymentRecord(
  receipt: ConsignmentReceiptDetail,
  shell?: PrintShell | null,
): void {
  const allocRows = receipt.allocations
    .map(
      (a) => `<tr>
        <td class="code">${esc(a.invoiceDocNo)}</td>
        <td class="right">${esc(peso(a.amount))}</td>
      </tr>`,
    )
    .join('');

  const body = `
  ${docHeader('PAYMENT RECORD', receipt.docNo, fmtDate(receipt.receivedAt), receipt.status === 'void')}
  <div class="grid">
    <div>
      <p class="label">Received from</p>
      <p style="font-weight:600;margin:0">${esc(receipt.customerName)}</p>
    </div>
    <div>
      <p class="label">Method</p>
      <p style="font-weight:600;margin:0;text-transform:capitalize">${esc(receipt.method)}</p>
      ${receipt.reference ? `<p class="code muted" style="font-size:12px;margin:4px 0 0">Ref: ${esc(receipt.reference)}</p>` : ''}
    </div>
  </div>
  <p class="label" style="margin-top:16px">Amount received</p>
  <p class="big-amount">${esc(peso(receipt.amount))}</p>
  ${
    allocRows
      ? `<p class="label" style="margin-top:16px">Applied to</p>
  <table>
    <thead><tr><th>Billing No</th><th class="right">Amount applied</th></tr></thead>
    <tbody>${allocRows}</tbody>
  </table>`
      : ''
  }
  ${memoBlock(receipt.memo)}
  <p class="foot">This is not an official receipt.</p>`;

  openPrintWindow(`Payment Record ${receipt.docNo}`, body, shell);
}

// ── 4. Statement of Account (SOA) ───────────────────────────────────────────
//
// `SoaLine.docType` arrives from the server ALREADY a display label
// ('Billing' | 'Payment' | 'Payment (void)' — see getCustomerSoa's UNION), so
// it is printed verbatim. Do NOT reintroduce a doc_type→label map here: the
// keys would never match, and the day someone "fixed" them 'Payment (void)'
// would collapse to 'Payment' and a voided receipt would read as a live one.

export function printSoa(soa: CustomerSoa): void {
  const period =
    soa.from || soa.to
      ? `${soa.from ? fmtDate(soa.from) : 'Beginning'} — ${soa.to ? fmtDate(soa.to) : fmtDate(new Date())}`
      : `As of ${fmtDate(new Date())}`;

  const zeroBlank = (v: string) => (Number(v) === 0 ? '' : peso(v));

  const rows = soa.lines
    .map(
      (l) => `<tr>
        <td>${esc(fmtDate(l.date))}</td>
        <td>${esc(l.docType)}</td>
        <td class="code">${esc(l.docNo)}</td>
        <td class="muted">${l.ref ? esc(l.ref) : ''}</td>
        <td class="right">${esc(zeroBlank(l.charges))}</td>
        <td class="right">${esc(zeroBlank(l.payments))}</td>
        <td class="right">${esc(peso(l.balance))}</td>
      </tr>`,
    )
    .join('');

  const body = `
  <header>
    <div>
      <h1>STATEMENT OF ACCOUNT</h1>
      <p class="muted" style="font-size:12px;margin:4px 0 0">${esc(period)}</p>
    </div>
    <div style="text-align:right">
      <p class="muted" style="font-size:12px;margin:0">Printed ${esc(fmtDate(new Date()))}</p>
    </div>
  </header>
  <div class="grid">
    <div>
      <p class="label">Consignee</p>
      <p style="font-weight:600;margin:0">${esc(soa.customer.name)}</p>
      <p class="muted" style="font-size:12px;margin:2px 0 0">${esc(soa.customer.email)}</p>
      ${soa.customer.phone ? `<p class="muted" style="font-size:12px;margin:2px 0 0">${esc(soa.customer.phone)}</p>` : ''}
    </div>
    <div>
      <p class="label">Opening balance</p>
      <p style="font-weight:600;margin:0">${esc(peso(soa.openingBalance))}</p>
    </div>
  </div>
  <table>
    <thead><tr>
      <th>Date</th><th>Doc</th><th>No</th><th>Ref</th>
      <th class="right">Charges</th><th class="right">Payments</th><th class="right">Balance</th>
    </tr></thead>
    <tbody>
      ${rows}
      <tr class="total">
        <td colspan="4">Totals</td>
        <td class="right">${esc(peso(soa.totalCharges))}</td>
        <td class="right">${esc(peso(soa.totalPayments))}</td>
        <td></td>
      </tr>
      <tr class="emph">
        <td colspan="6">CLOSING BALANCE</td>
        <td class="right">${esc(peso(soa.closingBalance))}</td>
      </tr>
    </tbody>
  </table>
  <p class="foot">This is not an official receipt.</p>`;

  openPrintWindow(`Statement of Account — ${soa.customer.name}`, body);
}
